import { useCallback, useMemo } from 'react';

import { Button, Tooltip } from 'antd';

import { Icon } from '@eduzz/ui-antd-theme';

import { ReceivedMessageAction, useMessageListener } from '@/hooks/useMessageListener';
import { useThrottleExecution } from '@/hooks/useThrottleExecution';
import { cn } from '@/lib/cn';
import useStepStore, { type Step } from '@/stores/steps';

import Logo from './Logo';

type HeaderProps = {
  onGoBack?: () => void;
  goBackHiddenContentList?: Step['content'][];
  logoSize?: 'base' | 'large';
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
};

const Header = ({ onGoBack, goBackHiddenContentList, prefix, suffix, logoSize }: HeaderProps) => {
  const throttleExecution = useThrottleExecution();

  const isLocked = useStepStore(state => state.isLocked);
  const currentContent = useStepStore(state => state.stepData?.content);
  const isGoBackAvailable = useMemo(() => {
    if (!onGoBack) {
      return false;
    }

    if (!currentContent) {
      return true;
    }

    return !goBackHiddenContentList?.includes(currentContent);
  }, [currentContent, goBackHiddenContentList, onGoBack]);

  const handleGoBack = useCallback(() => {
    if (isLocked) {
      return;
    }

    throttleExecution({
      callback: async () => {
        onGoBack?.();
      },
      delay: 500
    });
  }, [isLocked, onGoBack, throttleExecution]);

  useMessageListener({
    [ReceivedMessageAction.GO_BACK]: () => {
      handleGoBack();
    }
  });

  return (
    <header
      className={cn('bg-surface-default flex w-full items-center px-4 pt-2 pb-3 lg:py-5', {
        'lg:pt-4 lg:pb-3': logoSize === 'large'
      })}
    >
      <div className='mr-4 flex-1'>
        <Tooltip title='Voltar'>
          {isGoBackAvailable && (
            <Button
              color='default'
              variant='filled'
              shape='circle'
              id='header-go-back-button'
              onClick={handleGoBack}
              icon={<Icon.ArrowLeft />}
              disabled={isLocked}
            />
          )}
        </Tooltip>
        {prefix && <>{prefix}</>}
      </div>

      <div className='flex-1'>
        <Logo
          className={cn('mx-auto w-28 flex-1 shrink-0 lg:w-32', {
            'lg:w-41': logoSize === 'large'
          })}
        />
      </div>

      <div className='flex flex-1 justify-end gap-2'>{suffix}</div>
    </header>
  );
};

export default Header;
